import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';

import {
  List,
  Badge,
  Avatar,
  Typography,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  Stack,
  Button,
  styled,
} from '@mui/material';
import moment from 'moment';
import { useMutation } from '@apollo/client';

import { QUERY_UNSEEN_NOTIFICATIONS, MUTATION_SEEN_ALL_NOTIFICATION } from '../graphql/notification';
import Scrollbar from './Scrollbar';
import MenuPopover from './MenuPopover';
import { IconButtonAnimate } from './animate';
import Image from './Image';
import { PATH_DASHBOARD } from '../routes/paths';
import useGraphQuery from '../hooks/useGraphQuery';
import NotificationModal from './NotificationModal';
import Loading from './Loading';
import useRedux from '../hooks/useRedux';
import '../styles/ringBell.css';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data: unseenNotifications,
    refetch: refetchUnseenNotifications,
    fetching: loadingUnseenNotifications,
  } = useGraphQuery(QUERY_UNSEEN_NOTIFICATIONS, {
    pollInterval: 60 * 1000, // Refetch the query every 1 minute
  });
  const [onSeenAllMutation] = useMutation(MUTATION_SEEN_ALL_NOTIFICATION);
  // --------------
  const [open, setOpen] = useState(null);

  // --------------
  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  // --------------
  const handleClose = () => {
    setOpen(null);
  };

  // --------------
  const goToAll = () => {
    handleClose();
    navigate(PATH_DASHBOARD.notifications);
    refetchUnseenNotifications();
  };

  // --------------
  const onSeenAll = () => {
    onSeenAllMutation();
    refetchUnseenNotifications();
  };

  const [notificationModalShow, setNotificationModalShow] = useState(false);
  const [notificationsToShowInModal, setNoficationsToShowInModal] = useState([]);
  const [notificationFiredByClick, setNotificationFiredByClick] = useState(false);

  const { appSetting } = useRedux();
  const neverShowNotification = appSetting.NeverShowNotification;

  useEffect(() => {
    if (
      unseenNotifications?.notifications.unseenNotifications.some(
        (notification) => notification.ShouldPopup === true
      ) &&
      !neverShowNotification &&
      loadingUnseenNotifications === false
    ) {
      setNoficationsToShowInModal(unseenNotifications?.notifications.unseenNotifications);
      setNotificationFiredByClick(false);
      setNotificationModalShow(true);
    }
  }, [unseenNotifications, loadingUnseenNotifications, neverShowNotification]);

  // --------------
  return (
    <>
      <NotificationModal
        open={notificationModalShow}
        onClose={() => {
          setNotificationModalShow(false);
          setTimeout(refetchUnseenNotifications, 100);
        }}
        notifications={notificationsToShowInModal}
        clicked={notificationFiredByClick}
      />
      <IconButtonAnimate color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 36, height: 36 }}>
        <Badge badgeContent={unseenNotifications?.notifications?.unseenNotifications?.length || 0} color="error">
          <ReactSVG
            className={unseenNotifications?.notifications?.unseenNotifications?.length > 0 ? 'bell' : null}
            src="/icons/notification.svg"
            style={{ width: 24, height: 24 }}
          />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 300, px: 2, py: 1, mt: 1.5, ml: 0.75 }}
      >
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant="body2" fontSize={14}>
            {t('Notifications')}
          </Typography>

          <Button color="inherit" size="small" sx={{ typography: 'caption' }} onClick={onSeenAll}>
            {t('Clear')}
          </Button>
        </Stack>

        {loadingUnseenNotifications && (
          <div>
            <Loading />
          </div>
        )}
        {!loadingUnseenNotifications &&
          (unseenNotifications?.notifications?.unseenNotifications?.length > 0 ? (
            <Scrollbar sx={{ height: { xs: 340, sm: 'auto' }, my: 1 }}>
              <List disablePadding>
                {unseenNotifications.notifications.unseenNotifications.map((notification) => (
                  <NotificationItem
                    key={notification._id}
                    notification={notification}
                    openNotificationModal={() => {
                      setNotificationFiredByClick(true);
                      setNoficationsToShowInModal([notification]);
                      setNotificationModalShow(true);
                      handleClose();
                    }}
                  />
                ))}
              </List>
            </Scrollbar>
          ) : (
            <Typography variant="body2" color="#aaa" sx={{ textAlign: 'center', my: 8 }}>
              {t('No notification found')}
            </Typography>
          ))}

        <Button fullWidth sx={{ typography: 'caption' }} onClick={goToAll}>
          {t('See All Notifications')}
        </Button>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------
function NotificationItem({ notification, openNotificationModal }) {
  const { avatar, title } = renderContent(notification);

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 1,
        mt: '1px',
        borderRadius: 1,
      }}
      onClick={openNotificationModal}
    >
      <ListItemAvatar>
        {notification.online ? (
          <StyledBadge overlap="circular" anchorOrigin={{ vertical: 'top', horizontal: 'right' }} variant="dot">
            <Avatar sx={{ bgcolor: 'background.neutral', width: 45, height: 45 }}>{avatar}</Avatar>
          </StyledBadge>
        ) : (
          <Avatar sx={{ bgcolor: 'background.neutral', width: 45, height: 45 }}>{avatar}</Avatar>
        )}
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
              fontSize: 11,
            }}
          >
            {moment(notification.Date).fromNow()}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------
function renderContent(notification) {
  const title = (
    <>
      <Typography variant="body2" lineHeight={1} sx={{ mb: 1 }}>
        {notification.Title}
      </Typography>
      {/* <Typography component="span" variant="caption" fontSize={11} sx={{ color: 'text.secondary' }}>
        {notification.description}
      </Typography> */}
    </>
  );

  return {
    avatar: notification.avatar ? (
      <Image alt={notification.title} src={notification.avatar} />
    ) : (
      <Avatar sx={{ width: 40, height: 40 }}>{notification.Title[0]}</Avatar>
    ),
    title,
  };
}

// ----------------------------------------------------------------------

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.light,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2)',
      opacity: 0,
    },
  },
}));
